import { createContext, useMemo, useReducer, useEffect, useContext, useRef, useCallback, ReactNode } from "react"
import { produce } from "immer"
import { countBy, isPlainObject, isString } from "lodash"
import { v4 as uuidv4 } from "uuid"
import { usePreferences } from "hooks/use-preferences"
import { dashboardEvents, DASHBOARD_EVENTS } from "helpers/events/dashboard-events"
import { isModuleTypeRegistered } from "../modules/registry"
import { DEFAULT_FACTORS_THEMATIC_CONTENT_SET_ID } from "../modules/constants"
import { DefaultModuleDescriptor, MODULE_TYPES } from "../modules/types"
import { OK } from "helpers/data-statuses"
import { warn, info } from "helpers/dev-logger"
import { log } from "helpers/logger"
import {
  type ModuleType,
  type ModuleConfig,
  type DashboardModule,
  type ModulesState,
  type ModulesContextValue,
} from "../modules/types"
import { useContentSetsMetadata } from "resources/content-sets"

const ModulesContext = createContext<ModulesContextValue | undefined>(undefined)

const PREFERENCE_SUBTYPE = "modules"
const PREFERENCE_FIELDS = ["modules"]

// getDefaultModuleDescriptors() / createDefaultModules() are the single source of truth for first-load defaults.
// getDefaultState below only snapshots that result; keep them aligned when changing defaults.

const BASE_DEFAULT_MODULE_DESCRIPTORS: DefaultModuleDescriptor[] = [
  { type: MODULE_TYPES.ASSET_VIEW, initialView: "summary" },
  { type: MODULE_TYPES.GRAPH, initialView: "performance", title: "Performance Graph" },
  // KPI uses initialTab instead of initialView
  { type: MODULE_TYPES.KPI, initialTab: "summary", title: "KPI" },
  { type: MODULE_TYPES.KPI, initialTab: "decisions", title: "Decision" },
  { type: MODULE_TYPES.FACTORS, initialView: "risk", selectedTableLevels: ["style"] },
]

const THEMATIC_FACTORS_DEFAULT_DESCRIPTOR: DefaultModuleDescriptor = {
  type: MODULE_TYPES.FACTORS,
  initialView: DEFAULT_FACTORS_THEMATIC_CONTENT_SET_ID,
  sortMetricId: "netContribution",
  title: "Thematic Betas",
}

function getDefaultModuleDescriptors(hasThematicBetasContentSet?: boolean): DefaultModuleDescriptor[] {
  if (!hasThematicBetasContentSet) {
    return [...BASE_DEFAULT_MODULE_DESCRIPTORS]
  }
  return [...BASE_DEFAULT_MODULE_DESCRIPTORS, THEMATIC_FACTORS_DEFAULT_DESCRIPTOR]
}

function createDefaultModules(hasThematicBetasContentSet?: boolean): DashboardModule[] {
  return getDefaultModuleDescriptors(hasThematicBetasContentSet).map(({ type, ...config }) => ({
    id: uuidv4(),
    type,
    config,
  }))
}

function getDefaultState(hasThematicBetasContentSet?: boolean): ModulesState {
  return {
    modules: createDefaultModules(hasThematicBetasContentSet),
  }
}

// Multiset of module types matches getDefaultModuleDescriptors(hasThematicBetasContentSet); used after reset before running Dockview resetLayout.
export function isDefaultModulesSnapshot(
  modules: DashboardModule[],
  hasThematicBetasContentSet?: boolean,
): boolean {
  const expectedTypes = getDefaultModuleDescriptors(hasThematicBetasContentSet).map(
    descriptor => descriptor.type,
  )
  if (modules.length !== expectedTypes.length) {
    return false
  }

  const remainingTypeCounts = countBy(expectedTypes)

  for (const module of modules) {
    if (module.config?.isSecondary) {
      return false
    }
    const remainingCount = remainingTypeCounts[module.type] ?? 0
    if (remainingCount === 0) {
      return false
    }
    remainingTypeCounts[module.type] = remainingCount - 1
  }

  return Object.values(remainingTypeCounts).every(count => count === 0)
}

const ActionTypes = {
  SET_PREFERENCES: "SET_PREFERENCES",
  ADD_MODULE: "ADD_MODULE",
  REMOVE_MODULE: "REMOVE_MODULE",
  UPDATE_MODULE: "UPDATE_MODULE",
  RESET_MODULES_TO_DEFAULT: "RESET_MODULES_TO_DEFAULT",
} as const

type ModulesAction =
  | { type: typeof ActionTypes.SET_PREFERENCES, preferences: { modules?: DashboardModule[] }, hasThematicBetasContentSet?: boolean }
  | { type: typeof ActionTypes.ADD_MODULE, module: { id: string, type: ModuleType, config?: ModuleConfig } }
  | { type: typeof ActionTypes.REMOVE_MODULE, moduleId: string }
  | { type: typeof ActionTypes.UPDATE_MODULE, moduleId: string, updates: Partial<DashboardModule> }
  | { type: typeof ActionTypes.RESET_MODULES_TO_DEFAULT, hasThematicBetasContentSet?: boolean }

const addModule = (module: { id: string, type: ModuleType, config?: ModuleConfig }): ModulesAction => ({
  type: ActionTypes.ADD_MODULE,
  module,
})

const removeModule = (moduleId: string): ModulesAction => ({
  type: ActionTypes.REMOVE_MODULE,
  moduleId,
})

const updateModule = (moduleId: string, updates: Partial<DashboardModule>): ModulesAction => ({
  type: ActionTypes.UPDATE_MODULE,
  moduleId,
  updates,
})

interface ValidationResult {
  valid: DashboardModule[]
  isCorrected: boolean
}

function hasModuleShape(
  value: unknown,
): value is { id: string, type: string, config?: unknown } {
  if (!isPlainObject(value)) return false

  const obj = value as Record<string, unknown>
  return "id" in obj && "type" in obj && isString(obj.id) && isString(obj.type)
}

function isModuleConfig(value: unknown): value is ModuleConfig {
  return isPlainObject(value)
}

function validateModule(module: unknown): DashboardModule | null {
  try {
    if (!hasModuleShape(module)) return null

    if (!isModuleTypeRegistered(module.type)) {
      warn(`Module type "${module.type}" is not registered, removing module`)
      return null
    }

    let config: ModuleConfig = {}
    if (module.config !== undefined) {
      if (isModuleConfig(module.config)) {
        config = { ...module.config }
      } else {
        warn(`Module config must be an object, got ${typeof module.config}`)
      }
    }

    // Remove isPinning flag on load since no active operation exists after reload.
    if (config.isPinning) {
      warn(`Module ${module.id} has stale isPinning flag, cleaning up`)

      if (config.pinnedId) {
        config = { ...config, isPinning: false }
      } else {
        warn(`Module ${module.id} has isPinning=true but no pinnedId, removing incomplete module`)
        return null
      }
    }

    // Never persist duplicateFromPanelId: it is only for the initial copy. If we keep it,
    // useApplyDuplicatePreferences runs on every reload and can overwrite the duplicate's
    // saved preferences with the source panel's when the source query resolves after the
    // duplicate's own preferences, corrupting the duplicate's preferences.
    if (Object.prototype.hasOwnProperty.call(config, "duplicateFromPanelId")) {
      config = { ...config }
      delete config.duplicateFromPanelId
    }

    // `floating` is only used when adding a panel (open in a floating window). Drop it from
    // persisted module config — floating placement is stored in the layout snapshot, not here.
    if (Object.prototype.hasOwnProperty.call(config, "floating")) {
      config = { ...config }
      delete config.floating
    }

    // Remove AI artifact modules where pinning failed
    if (
      module.type === MODULE_TYPES.AI_ARTIFACT
      && config.hasError === true
      && !config.pinnedId
    ) {
      warn(`Module ${module.id} has hasError=true but no pinnedId, removing failed pin module`)
      return null
    }

    const validatedModule: DashboardModule = {
      id: module.id,
      type: module.type,
      config,
    }

    return validatedModule
  } catch (error) {
    log(error)
    return null
  }
}

function validateModuleType(type: string, action: string): type is ModuleType {
  if (!isModuleTypeRegistered(type)) {
    log(`Cannot ${action} module: type "${type}" is not registered`)
    return false
  }
  return true
}

function validateModules(modules: unknown, hasThematicBetasContentSet?: boolean): ValidationResult {
  const validModules = Array.isArray(modules)
    ? modules.reduce<DashboardModule[]>((acc, module) => {
      const validModule = validateModule(module)
      if (validModule) acc.push(validModule)
      return acc
    }, [])
    : []

  const isCorrected = !Array.isArray(modules) || validModules.length !== modules.length

  // Apply defaults if we ended up with no valid modules
  if (validModules.length === 0) {
    return { valid: createDefaultModules(hasThematicBetasContentSet), isCorrected: true }
  }

  return { valid: validModules, isCorrected }
}

function reducer(state: ModulesState, action: ModulesAction): ModulesState {
  return produce(state, (draft) => {
    switch (action.type) {
      case ActionTypes.SET_PREFERENCES: {
        if ("modules" in action.preferences) {
          const { valid } = validateModules(action.preferences.modules, action.hasThematicBetasContentSet)
          draft.modules = valid
        }
        break
      }

      case ActionTypes.ADD_MODULE: {
        const { id, type, config = {} } = action.module

        if (!validateModuleType(type, "add")) {
          break
        }

        draft.modules.push({ id, type, config })
        break
      }

      case ActionTypes.REMOVE_MODULE:
        draft.modules = draft.modules.filter((m) => m.id !== action.moduleId)
        break

      case ActionTypes.UPDATE_MODULE: {
        const index = draft.modules.findIndex((m) => m.id === action.moduleId)
        if (index !== -1) {
          const currentModule = draft.modules[index]
          const updates = action.updates

          if ("type" in updates && updates.type !== undefined) {
            if (!validateModuleType(updates.type, "update")) {
              break
            }
          }

          // Shallow merge to avoid stale properties. (e.g. dataGridState, columnVisibilityModel)
          const { config: configUpdates, ...topLevelUpdates } = updates
          Object.assign(currentModule, topLevelUpdates)
          if (configUpdates !== undefined) {
            currentModule.config = { ...currentModule.config, ...configUpdates }
          }
        }
        break
      }

      case ActionTypes.RESET_MODULES_TO_DEFAULT:
        draft.modules = createDefaultModules(action.hasThematicBetasContentSet)
        break
    }
  })
}

export function ModulesProvider({ children }: { children: ReactNode }) {
  const [contentSetsMetadata, contentSetsMetadataStatus] = useContentSetsMetadata()
  const hasThematicBetasContentSet = useMemo(() => {
    if (contentSetsMetadataStatus === OK && contentSetsMetadata) {
      return contentSetsMetadata.some(({ id }: { id: string }) => id === DEFAULT_FACTORS_THEMATIC_CONTENT_SET_ID)
    }
    return false
  }, [contentSetsMetadata, contentSetsMetadataStatus])
  const defaultState = useMemo(() => getDefaultState(hasThematicBetasContentSet), [hasThematicBetasContentSet])
  const [state, dispatch] = useReducer(reducer, defaultState)
  const setPreferences = useCallback(
    (preferences: { modules?: DashboardModule[] }): ModulesAction => ({
      type: ActionTypes.SET_PREFERENCES,
      preferences,
      hasThematicBetasContentSet,
    }),
    [hasThematicBetasContentSet],
  )
  const hasValidatedRef = useRef(false)

  // Capture initial module IDs to detect if server preferences were loaded
  const initialModuleIdsRef = useRef(
    defaultState.modules.map(m => m.id).join(","),
  )

  const isPreferencesLoaded = usePreferences(
    PREFERENCE_SUBTYPE,
    PREFERENCE_FIELDS,
    state,
    dispatch,
    setPreferences,
  )

  // Validate and clean up stale isPinning flags only once on initial load
  useEffect(() => {
    if (!isPreferencesLoaded) return
    if (hasValidatedRef.current) return

    hasValidatedRef.current = true

    const { valid, isCorrected } = validateModules(state.modules, hasThematicBetasContentSet)

    // If module IDs haven't changed since mount, no server preferences were loaded
    const currentModuleIds = state.modules.map(m => m.id).join(",")
    const stateUnchangedSinceMount = currentModuleIds === initialModuleIdsRef.current

    if (stateUnchangedSinceMount) {
      dispatch(setPreferences({ modules: state.modules }))
    } else if (isCorrected) {
      info("Dashboard modules were corrected during validation")
      dispatch(setPreferences({ modules: valid }))
    }
    // Only run once when preferences are loaded
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPreferencesLoaded])

  // Content sets resolve after first paint; useReducer keeps the initial snapshot without thematic.
  // When entitlement becomes known, upgrade users who still match the default-without-thematic layout.
  useEffect(() => {
    if (!isPreferencesLoaded || !hasThematicBetasContentSet) {
      return
    }
    const hasDefaultSnapshotWithThematicBetas = isDefaultModulesSnapshot(state.modules, true)
    if (hasDefaultSnapshotWithThematicBetas) {
      return
    }
    const hasDefaultSnapshotWithoutThematicBetas = isDefaultModulesSnapshot(state.modules, false)
    if (!hasDefaultSnapshotWithoutThematicBetas) {
      return
    }
    dispatch(
      addModule({
        id: uuidv4(),
        type: MODULE_TYPES.FACTORS,
        config: {
          initialView: DEFAULT_FACTORS_THEMATIC_CONTENT_SET_ID,
          sortMetricId: "netContribution",
          title: "Thematic Betas",
        },
      }),
    )
  }, [isPreferencesLoaded, hasThematicBetasContentSet, state.modules, dispatch])

  const moduleActions = useMemo(
    () => ({
      addModule: (module: { type: ModuleType, config?: ModuleConfig }): string => {
        const id = uuidv4()
        dispatch(addModule({ ...module, id }))
        return id
      },
      removeModule: (module: DashboardModule) => {
        dashboardEvents.emit(DASHBOARD_EVENTS.MODULE_REMOVED, module)
        dispatch(removeModule(module.id))
      },
      updateModule: (moduleId: string, updates: Partial<DashboardModule>) => {
        dispatch(updateModule(moduleId, updates))
      },
      resetModulesToDefault: () => {
        dispatch({ type: ActionTypes.RESET_MODULES_TO_DEFAULT, hasThematicBetasContentSet })
      },
    }),
    [dispatch, hasThematicBetasContentSet],
  )

  const contextValue = useMemo<ModulesContextValue>(
    () => ({
      modules: state.modules,
      isPreferencesLoaded,
      hasThematicBetasContentSet,
      ...moduleActions,
    }),
    [state.modules, isPreferencesLoaded, hasThematicBetasContentSet, moduleActions],
  )

  return (
    <ModulesContext.Provider value={contextValue}>
      {children}
    </ModulesContext.Provider>
  )
}

export function useModules(): ModulesContextValue {
  const context = useContext(ModulesContext)
  if (!context) {
    throw new Error("useModules must be used within ModulesProvider")
  }
  return context
}
